import React, { useMemo } from 'react';

export default function StatsViewer({ 
  table, 
  selectedCol, 
  searchIndices, 
  viewType, 
  pieConfig, 
  colorConfig,
  excludeNulls,
  activeCategory,
  onCategoryClick
}) {
  const statsData = useMemo(() => {
    if (!table || !selectedCol) return [];
    const col = table.getChild(selectedCol);
    // Use searchIndices directly; they are already filtered by date/space/other stats
    const source = searchIndices || Array.from({ length: table.numRows }, (_, i) => i);

    const counts = new Map();
    for (const i of source) {
      const val = col.get(i);
      const isNullOrEmpty = val == null || val === '';
      
      if (excludeNulls && isNullOrEmpty) continue;
      const key = isNullOrEmpty ? 'null' : String(val);

      counts.set(key, (counts.get(key) || 0) + 1);
    }

    const sorted = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
    const top15 = sorted.slice(0, 15);
    const others = sorted.slice(15);

    if (others.length > 0) {
      const othersCount = others.reduce((sum, [_, count]) => sum + count, 0);
      top15.push(['Other', othersCount]);
    }

    return top15;
  }, [table, selectedCol, searchIndices, excludeNulls]);

  // Helper to convert the [r,g,b,a] from our stable map to a CSS string
  const getEntryColor = (val) => {
    if (val === 'Other') return 'rgba(0, 0, 0, 0.32)';
    if (!colorConfig) return 'rgba(100,100,100,0.5)';
    const entry = [...colorConfig.colorMap.entries()].find(([k]) => String(k) === String(val));
    const rgba = entry ? entry[1] : [100, 100, 100, 200];
    return `rgba(${rgba[0]}, ${rgba[1]}, ${rgba[2]}, ${Math.max(rgba[3] / 255, 0.1)})`;
  };

  const total = statsData.reduce((sum, [_, count]) => sum + count, 0);
  const maxCount = Math.max(...statsData.map(d => d[1]), 0);

  const getDonutPath = (startPercent, endPercent) => {
    const { angle, rotation, innerRadius, outerRadius } = pieConfig;
    
    // SVG arcs fail if the start and end points are identical.
    // Subtract a tiny fraction if a slice spans the entire requested angle.
    const isFullSweep = (endPercent - startPercent) >= 0.999 && angle >= 360;

  // If it's a full 100% circle, draw a seamless circle to avoid the radial "seam" line
  if (isFullSweep) {
    const innerPath = innerRadius > 0 
      ? `M 0 ${-innerRadius} A ${innerRadius} ${innerRadius} 0 1 0 0 ${innerRadius} A ${innerRadius} ${innerRadius} 0 1 0 0 ${-innerRadius}` 
      : '';
    return `M 0 ${-outerRadius} A ${outerRadius} ${outerRadius} 0 1 1 0 ${outerRadius} A ${outerRadius} ${outerRadius} 0 1 1 0 ${-outerRadius} ${innerPath} Z`;
  }

  const effectiveEndPercent = endPercent; 
  const startAngle = (startPercent * angle + rotation) * (Math.PI / 180);
  const endAngle = (effectiveEndPercent * angle + rotation) * (Math.PI / 180);
  
  const x1_out = outerRadius * Math.cos(startAngle);
  const y1_out = outerRadius * Math.sin(startAngle);
  const x2_out = outerRadius * Math.cos(endAngle);
  const y2_out = outerRadius * Math.sin(endAngle);
  
  const x1_in = innerRadius * Math.cos(startAngle);
  const y1_in = innerRadius * Math.sin(startAngle);
  const x2_in = innerRadius * Math.cos(endAngle);
  const y2_in = innerRadius * Math.sin(endAngle);
  
  const largeArc = (endPercent - startPercent) * angle > 180 ? 1 : 0;

  return [
    `M ${x1_in} ${y1_in}`,
    `L ${x1_out} ${y1_out}`,
    `A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${x2_out} ${y2_out}`,
    `L ${x2_in} ${y2_in}`,
    `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x1_in} ${y1_in}`,
    'Z'
  ].join(' ');
};

  let cumulativePercent = 0;

  return (
    <div className="flex flex-row items-stretch w-full h-full overflow-hidden">
      
      {/* Left side: Chart */}
      <div className={`${viewType === 'pie' ? 'basis-1/2' : 'flex-1 w-full'} flex items-center justify-center overflow-hidden relative group`}>
        {viewType === 'pie' ? (
          <svg viewBox="-1.1 -1.1 2.2 2.2" className="w-full h-full max-w-full max-h-full">
            {statsData.map(([label, count]) => {
              const startP = cumulativePercent;
              const sliceP = total > 0 ? count / total : 0;
              cumulativePercent += sliceP;
              
              // Interaction states
              const isInteractive = label !== 'Other';
              const isSelected = activeCategory === label;
              const isFaded = activeCategory && !isSelected;

              return (
                <path 
                  key={label} 
                  d={getDonutPath(startP, cumulativePercent)} 
                  fill={getEntryColor(label)} 
                  stroke="#080000" 
                  strokeWidth={isSelected ? "0.025" : "0.005"} 
                  onClick={() => isInteractive && onCategoryClick && onCategoryClick(label)}
                  className={`outline-none ${isInteractive ? 'cursor-pointer hover:opacity-80' : ''}`}
                  style={{ opacity: isFaded ? 0.25 : 1 }}
                />
              );
            })}
          </svg>
        ) : (
          <div className="w-full h-full flex flex-col justify-around gap-1 py-4 pr-2 pl-1 overflow-y-auto">
            {statsData.map(([label, count]) => {
              const isInteractive = label !== 'Other';
              const isSelected = activeCategory === label;
              const isFaded = activeCategory && !isSelected;

              return (
                <div 
                  key={label} 
                  onClick={() => isInteractive && onCategoryClick && onCategoryClick(label)}
                  className={`w-full flex flex-col ${isInteractive ? 'cursor-pointer hover:bg-gray-100 rounded-md' : ''} ${isSelected ? 'bg-blue-50 ring-1 ring-blue-400 p-1 rounded-md' : 'p-1'}`}
                  style={{ opacity: isFaded ? 0.35 : 1 }}
                >
                  <div className="flex text-[12px] px-0 font-bold text-gray-500">
                    <span className="truncate mr-2 text-gray-700">{label}</span>
                    <span className="ml-4 text-gray-500 font-mono">{count}</span>
                    <span className="ml-4 -translate-y-[1px] text-gray-400 font-normal">{total > 0 ? ((count / total) * 100).toFixed(1) : '0.0'}%</span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-[3px] overflow-hidden w-full">
                    <div 
                      className="h-full rounded-[3px]" 
                      style={{ width: `${maxCount > 0 ? (count / maxCount) * 100 : 0}%`, backgroundColor: getEntryColor(label) }} 
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Right side: Legend (for pie charts) */}
      {viewType === 'pie' && (
      <div className="basis-1/2 shrink-0 flex flex-col overflow-y-auto pl-4 pr-1 pt-1 min-w-0">
        <div className="my-auto w-full">
          {statsData.map(([label, count]) => {
            const isInteractive = label !== 'Other';
            const isSelected = activeCategory === label;
            const isFaded = activeCategory && !isSelected;

            return (
              <div 
                key={label} 
                title={label} 
                onClick={() => isInteractive && onCategoryClick && onCategoryClick(label)}
                className={`flex items-center gap-2 text-[10px] mb-1.5 w-full ${isInteractive ? 'cursor-pointer hover:bg-gray-100 rounded-md -ml-1 pl-1' : ''} ${isSelected ? 'font-black bg-blue-50 ring-1 ring-blue-300 rounded-md p-1 -ml-1' : ''}`}
                style={{ opacity: isFaded ? 0.4 : 1 }}
              >
                <div className="w-3 h-3 rounded-sm shrink-0 border border-gray-800" style={{ backgroundColor: getEntryColor(label) }} />
                <span className={`truncate flex-grow ${isSelected ? 'text-blue-900 font-bold' : 'text-gray-700 font-semibold'}`}>{label}</span>
                <span className="text-gray-500 font-mono shrink-0 ml-auto">{count}</span>
                <span className="text-gray-400 text-[9px] w-15 shrink-0 text-right">({total > 0 ? ((count / total) * 100).toFixed(1) : '0.0'}%)</span>
              </div>
            );
          })}
        </div>
      </div>
      )}
    </div>
  );
}